import { useEffect, useEffectEvent, useRef, useState } from "react";
import { useFormikContext } from "formik";

import { type ErrorType } from "@vega-api/configs/customClientQuery";
import { useUpdateDealTeaserEmailV2 } from "@vega-api/generated/coinvest-deal-teaser-email-v2/coinvest-deal-teaser-email-v2";
import { m } from "@vega-core/paraglide/messages";
import { formatOnlyTime } from "@vega-core/utils/formatDate";
import { htmlToPlainText } from "@vega-core/utils/richText";
import { useDebounce } from "@vega-core/utils/useDebounce";
import { useCoInvestErrorToast } from "@vega-modules/hooks/useCoInvestErrorToast";

import {
  buildDraftUpdatePayload,
  DRAFT_AUTOSAVE_MUTATION_KEY,
  type DraftEmailFormValues,
  serializeDraftPayload,
} from "../DraftEmail/draftEmailUtils";

export const AUTOSAVE_DELAY_MS = 700;

const isPersistable = (values: DraftEmailFormValues): boolean =>
  !!values.subject.trim() && !!htmlToPlainText(values.body);

type AutoSaveIndicatorProps = {
  isSaving: boolean;
  hasError: boolean;
  lastSavedAt: Date | null;
};

function AutoSaveIndicator({
  isSaving,
  hasError,
  lastSavedAt,
}: AutoSaveIndicatorProps) {
  if (isSaving) {
    return (
      <span
        className="body-small-regular text-text/secondary"
        data-testid="autosave-indicator-saving"
      >
        {m.coInvestments_sendDrafts_autoSave_saving()}
      </span>
    );
  }

  if (hasError) {
    return (
      <span
        className="body-small-regular text-text/negative"
        data-testid="autosave-indicator-error"
      >
        {m.coInvestments_sendDrafts_autoSave_error()}
      </span>
    );
  }

  if (lastSavedAt) {
    return (
      <span
        className="body-small-regular text-text/secondary"
        data-testid="autosave-indicator-saved"
      >
        {m.coInvestments_sendDrafts_autoSave_saved({
          time: formatOnlyTime(lastSavedAt.toISOString()),
        })}
      </span>
    );
  }

  return null;
}

type DraftAutoSaveProps = {
  dealId: string;
  emailId: string;
  knownUserIds: ReadonlySet<string>;
  defaultRoleId: string;
  onSaved: () => void;
};

export function DraftAutoSave({
  dealId,
  emailId,
  knownUserIds,
  defaultRoleId,
  onSaved,
}: DraftAutoSaveProps) {
  const { values, resetForm } = useFormikContext<DraftEmailFormValues>();
  const showErrorToast = useCoInvestErrorToast();
  const debounced = useDebounce(values, AUTOSAVE_DELAY_MS);

  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);
  const [hasError, setHasError] = useState(false);

  const lastSentPayloadRef = useRef<string | undefined>(undefined);

  if (lastSentPayloadRef.current === undefined) {
    lastSentPayloadRef.current = serializeDraftPayload({
      values,
      knownUserIds,
      defaultRoleId,
    });
  }
  const lastGoodValuesRef = useRef(values);
  const saveQueueRef = useRef<Promise<void>>(Promise.resolve());

  const { mutateAsync, isPending } = useUpdateDealTeaserEmailV2({
    mutation: { mutationKey: DRAFT_AUTOSAVE_MUTATION_KEY },
  });

  const persistDraft = async (
    next: DraftEmailFormValues,
    updateIndicator: boolean,
  ) => {
    const data = buildDraftUpdatePayload({
      values: next,
      knownUserIds,
      defaultRoleId,
    });
    const serializedPayload = JSON.stringify(data);

    if (serializedPayload === lastSentPayloadRef.current) return;

    if (!isPersistable(next)) return;

    lastSentPayloadRef.current = serializedPayload;

    try {
      await mutateAsync({
        dealId,
        emailId,
        data,
      });
      lastGoodValuesRef.current = next;

      if (updateIndicator) {
        setLastSavedAt(new Date());
        setHasError(false);
      }
      onSaved();
    } catch (error) {
      if (updateIndicator) {
        setHasError(true);
        resetForm({ values: lastGoodValuesRef.current });
      }
      showErrorToast(error as ErrorType<unknown>);
      lastSentPayloadRef.current = serializeDraftPayload({
        values: lastGoodValuesRef.current,
        knownUserIds,
        defaultRoleId,
      });
      onSaved();
    }
  };

  const enqueueSave = useEffectEvent(
    (next: DraftEmailFormValues, updateIndicator: boolean) => {
      saveQueueRef.current = saveQueueRef.current.then(() =>
        persistDraft(next, updateIndicator),
      );
    },
  );

  const flushOnUnmount = useEffectEvent(() => {
    if (
      serializeDraftPayload({ values, knownUserIds, defaultRoleId }) !==
        lastSentPayloadRef.current &&
      isPersistable(values)
    ) {
      enqueueSave(values, false);
    }
  });

  useEffect(() => {
    enqueueSave(debounced, true);
  }, [debounced]);

  useEffect(() => () => flushOnUnmount(), []);

  return (
    <AutoSaveIndicator
      hasError={hasError}
      isSaving={isPending}
      lastSavedAt={lastSavedAt}
    />
  );
}
